<template>
  <div v-if="!rows.length" data-testid="top-products-ranking-compare-empty">
    <ReviewsEmptyView
      class="py-8"
      :title="t('ProductTop100Ranking.compare.emptyTitle')"
      :description="t('ProductTop100Ranking.compare.emptyBody')"
    />
  </div>
  <div v-else class="flex flex-col gap-2">
    <div class="overflow-x-auto">
      <TableLight class="w-full min-w-[36rem] table-fixed [&_td]:align-middle">
        <colgroup>
          <col class="w-22" />
          <col />
          <col class="w-32" />
          <col class="w-42" />
          <col class="w-16" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">{{ t('ProductTop100Ranking.table.rank') }}</th>
            <th scope="col">{{ t('ProductTop100Ranking.table.tool') }}</th>
            <th scope="col">{{ t('ProductTop100Ranking.table.stars') }}</th>
            <th scope="col">{{ t('ProductTop100Ranking.table.reviews') }}</th>
            <th scope="col">
              <span class="sr-only">{{ t('ProductTop100Ranking.compare.remove') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.tool"
            :class="{ 'bg-primary-subtle text-primary': isOwn(row) }"
            :data-testid="isOwn(row) ? 'top-products-ranking-compare-own' : 'top-products-ranking-compare-row'"
          >
            <td class="tabular-nums" :class="{ 'txt-body-s-bold': isOwn(row) }">{{ row.rank ?? '–' }}</td>
            <td class="txt-body-s-bold" data-testid="top-products-ranking-compare-title">
              <span class="block truncate">{{ row.toolTitle }}</span>
            </td>
            <td>
              <span class="inline-flex items-center gap-1">
                <StarIcon class="lucide-icon-sm text-icon-secondary" fill="currentColor" />
                {{ formatStars(row.rating) }}
              </span>
            </td>
            <td class="text-right tabular-nums">{{ formatCount(row.reviewCount) }}</td>
            <td class="text-right">
              <IconButton
                v-if="!isOwn(row)"
                :icon="XIcon"
                size="extra-small"
                variant="ghost-neutral"
                :aria-label="t('ProductTop100Ranking.compare.remove')"
                data-testid="top-products-ranking-compare-remove"
                @click="emit('remove', row.tool)"
              />
            </td>
          </tr>
        </tbody>
      </TableLight>
    </div>
    <p class="text-body-s text-subtle" data-testid="top-products-ranking-compare-footer">
      {{ t('ProductTop100Ranking.compare.count', rows.length) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { IconButton } from '@ramp106/omrjs-core-ui'
import { StarIcon, XIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewsEmptyView from '@/components/ReviewsEmptyView.vue'
import TableLight from '@/ui/TableLight/TableLight.vue'

import type { TopProductsRankingCompareEntry, TopProductsRankingEntry } from './types'

const props = defineProps<{
  ownEntry?: TopProductsRankingEntry
  selection: TopProductsRankingCompareEntry[]
}>()

const emit = defineEmits<{
  (e: 'remove', tool: string): void
}>()

const { t, n } = useI18n()

const rows = computed<TopProductsRankingCompareEntry[]>(() => {
  if (!props.selection.length) return []
  const own = props.ownEntry
    ? [
        {
          tool: props.ownEntry.tool,
          toolTitle: props.ownEntry.toolTitle ?? props.ownEntry.tool,
          rank: props.ownEntry.rank ?? null,
          rating: props.ownEntry.rating ?? null,
          reviewCount: props.ownEntry.reviewCount ?? null,
        },
      ]
    : []
  return [...own, ...props.selection].sort((a, b) => (a.rank ?? Number.POSITIVE_INFINITY) - (b.rank ?? Number.POSITIVE_INFINITY))
})

function isOwn(row: TopProductsRankingCompareEntry): boolean {
  return !!props.ownEntry && row.tool === props.ownEntry.tool
}

function formatStars(rating: number | null | undefined): string {
  return rating == null ? '–' : n(rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function formatCount(count: number | null | undefined): string {
  return count == null ? '–' : n(count)
}
</script>
